import React, { useEffect, useMemo, useState } from 'react';
import { Edit, Monitor, RefreshCw, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import DataTable from '../components/DataTable';
import EmptyState from '../components/EmptyState';
import LoadingSkeleton from '../components/LoadingSkeleton';
import StatusBadge from '../components/StatusBadge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { devicesApi } from '../lib/api';

function formatTimestamp(value) {
  if (!value) return 'Never';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

export default function Devices() {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showEdit, setShowEdit] = useState(false);
  const [editDevice, setEditDevice] = useState(null);
  const pageSize = 12;

  const loadDevices = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const response = await devicesApi.list({ page, page_size: pageSize });
      setDevices(response.data?.items || []);
      setTotal(response.data?.total || 0);
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to load devices.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDevices();
  }, [page]);

  const filteredDevices = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return devices;
    return devices.filter((device) => device.hostname?.toLowerCase().includes(query));
  }, [devices, search]);

  const onlineCount = devices.filter((device) => device.status === 'online').length;
  const offlineCount = devices.filter((device) => device.status === 'offline').length;

  return (
    <div className="min-h-screen space-y-6 bg-slate-900 p-6 text-slate-300" data-testid="devices-page">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Devices</h1>
          <p className="mt-2 text-slate-500">
            Endpoint inventory, live status, and quick access to each device timeline.
          </p>
        </div>
        <Button className="bg-blue-600 text-white hover:bg-blue-700" onClick={() => loadDevices({ silent: true })}>
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card className="border-slate-700 bg-slate-800 shadow-lg">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Visible devices</p>
            <p className="mt-2 text-3xl font-bold text-white">{devices.length}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-700 bg-slate-800 shadow-lg">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Online</p>
            <p className="mt-2 text-3xl font-bold text-emerald-400">{onlineCount}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-700 bg-slate-800 shadow-lg">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Offline</p>
            <p className="mt-2 text-3xl font-bold text-rose-400">{offlineCount}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-700 bg-slate-800 shadow-lg">
        <CardHeader className="pb-0">
          <CardTitle className="text-white">Inventory</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by hostname"
              className="w-full rounded-lg border border-slate-600 bg-slate-700 py-3 pl-10 pr-4 text-white placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {error ? (
            <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4 text-sm text-red-300">
              {error}
            </div>
          ) : null}

          {loading ? (
            <LoadingSkeleton type="table" />
          ) : filteredDevices.length ? (
            <DataTable
              columns={[
                { key: 'hostname', label: 'Hostname' },
                {
                  key: 'os',
                  label: 'OS',
                  render: (value) => <span className="capitalize">{value || 'Unknown'}</span>,
                },
                {
                  key: 'status',
                  label: 'Status',
                  render: (value) => <StatusBadge status={value || 'offline'} />,
                },
                {
                  key: 'ip',
                  label: 'IP Address',
                  render: (_, row) => row.ip_address || row.ip || '—',
                },
                {
                  key: 'last_seen_at',
                  label: 'Last Seen',
                  render: (value) => formatTimestamp(value),
                },
                {
                  key: 'agent_version',
                  label: 'Agent Version',
                  render: (value) => value || '—',
                },
                {
                  key: 'actions',
                  label: 'Actions',
                  render: (_, row) => (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-slate-600 bg-slate-900 text-slate-300 hover:bg-slate-700"
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate(`/devices/${row.id}`);
                        }}
                      >
                        Timeline
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-slate-600 bg-slate-900 text-slate-300 hover:bg-slate-700"
                        onClick={(event) => {
                          event.stopPropagation();
                          setEditDevice(row);
                          setShowEdit(true);
                        }}
                      >
                        <Edit className="h-4 w-4" />
                        Rename
                      </Button>
                    </div>
                  ),
                },
              ]}
              data={filteredDevices}
              loading={loading}
              emptyMessage="No devices match the current search."
              pagination={{
                page,
                pageSize,
                total,
                onPageChange: setPage,
              }}
              onRowClick={(row) => navigate(`/devices/${row.id}`)}
            />
          ) : (
            <EmptyState
              icon={<Monitor className="h-8 w-8" />}
              title="No devices found"
              description="Adjust the search or enroll additional devices to populate the inventory."
            />
          )}
        </CardContent>
      </Card>

      <EditDeviceDialog
        device={editDevice}
        open={showEdit}
        onOpenChange={setShowEdit}
        onSuccess={loadDevices}
      />
    </div>
  );
}

function EditDeviceDialog({ device, open, onOpenChange, onSuccess }) {
  const [hostname, setHostname] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (device) {
      setHostname(device.hostname || '');
    }
  }, [device]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await devicesApi.update(device.id, { hostname });
      toast.success('Device renamed');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to rename device');
    } finally {
      setLoading(false);
    }
  };

  if (!device) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-slate-700 bg-slate-800 text-slate-300">
        <DialogHeader>
          <DialogTitle className="text-white">Rename Device: {device.hostname}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-4">
          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase text-slate-500">Hostname</label>
            <input
              value={hostname}
              onChange={(e) => setHostname(e.target.value)}
              className="w-full rounded-lg border border-slate-700 bg-slate-900 px-4 py-3 text-white outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <Button type="submit" className="w-full bg-blue-600 text-white hover:bg-blue-700" disabled={loading}>
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
